import { useContext, useEffect, useRef } from 'react'
import { RealtimeContext } from './contexts.js'

export function useRealtime() {
  const ctx = useContext(RealtimeContext)
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider')
  return ctx
}

// Subscribe to a realtime topic for the lifetime of the component.
//
//   useSubscription(`user.${clerkUserId}.messages`, (evt) => { ... })
//
// - evt is { type, topic, data } (docs/api-contract.md §6.1 envelope).
// - topic may be null/undefined while ids are still loading — treated as disabled.
// - options: { enabled = true, onReconnect } — onReconnect fires after the
//   connection drops, reopens, and the topic set is re-subscribed (refetch there).
// - Handlers are held in refs: passing a fresh closure every render does not
//   resubscribe; only manager/topic/enabled changes do.
//
// Returns { status, isConnected } for the shared connection
// (status: idle | connecting | open | reconnecting | closed).
export function useSubscription(topic, onEvent, options = {}) {
  const { manager, status } = useRealtime()
  const enabled = options.enabled !== false && !!topic

  const onEventRef = useRef(onEvent)
  const onReconnectRef = useRef(options.onReconnect)
  useEffect(() => {
    onEventRef.current = onEvent
  })
  useEffect(() => {
    onReconnectRef.current = options.onReconnect
  })

  useEffect(() => {
    if (!enabled) return undefined
    return manager.subscribe(
      topic,
      (evt) => onEventRef.current?.(evt),
      () => onReconnectRef.current?.(),
    )
  }, [manager, topic, enabled])

  return { status, isConnected: status === 'open' }
}
